import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { Check, Loader2, ExternalLink, Phone, Mail } from "lucide-react";
import { Logo } from "../components/Logo";
import { PreviewValidation } from "../components/PreviewValidation";

const STEP_LABELS = {
  nouvelle: { title: "Demande reçue", desc: "Nous avons bien reçu votre projet et l'analysons." },
  contactee: { title: "Premier échange", desc: "Nous vous avons contacté pour cadrer votre site." },
  devis_envoye: { title: "Devis envoyé", desc: "Votre devis est entre vos mains, à valider." },
  signee: { title: "Projet validé", desc: "C'est parti : la production démarre." },
  en_construction: { title: "Site en construction", desc: "Nous créons votre site avec vos contenus." },
  publie: { title: "Site publié", desc: "Votre site est en ligne !" },
};

const formatDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const Step = ({ id, index, current, isLast }) => {
  const done = index < current;
  const active = index === current;
  return (
    <li className="relative flex gap-4 pb-8 last:pb-0" data-testid={`track-step-${id}`} data-state={done ? "done" : active ? "active" : "todo"}>
      {!isLast && <span className={`absolute left-[15px] top-8 h-[calc(100%-2rem)] w-px ${done ? "bg-gold" : "bg-navy/15"}`} />}
      <span
        className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
          done ? "border-gold bg-gold text-navy" : active ? "border-gold bg-navy text-gold shadow-lg shadow-gold/30" : "border-navy/15 bg-white text-slate-400"
        }`}
      >
        {done ? <Check size={14} strokeWidth={3} /> : <span className="font-mono text-[11px] font-bold">{index + 1}</span>}
      </span>
      <div className={active ? "" : done ? "opacity-80" : "opacity-50"}>
        <p className={`font-display text-base font-bold ${active ? "text-navy" : "text-slate-700"}`}>{STEP_LABELS[id].title}</p>
        <p className="mt-0.5 text-sm text-slate-600">{STEP_LABELS[id].desc}</p>
        {active && <span className="mt-2 inline-block rounded-full bg-gold/15 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-gold-dark">Étape en cours</span>}
      </div>
    </li>
  );
};

export default function Tracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/track/${token}`)
      .then((res) => {
        setData(res.data);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [token]);

  return (
    <div className="min-h-screen bg-cream px-4 py-10 sm:py-16" data-testid="tracking-page">
      <div className="mx-auto max-w-2xl">
        <div className="flex justify-center"><Link to="/" aria-label="site.com"><Logo /></Link></div>
        <div className="mt-8 rounded-3xl border border-navy/10 bg-white p-6 shadow-xl shadow-navy/10 sm:p-10">
          {state === "loading" && (
            <div className="flex items-center justify-center gap-3 py-10 text-slate-500" data-testid="tracking-loading">
              <Loader2 className="animate-spin text-gold-dark" size={22} /> Chargement de votre suivi…
            </div>
          )}
          {state === "error" && (
            <div className="py-6 text-center" data-testid="tracking-error">
              <h1 className="font-display text-2xl font-bold text-navy">Suivi introuvable</h1>
              <p className="mt-3 text-sm text-slate-600">Ce lien de suivi n'est pas valide. Retrouvez-le dans nos emails ou contactez-nous.</p>
            </div>
          )}
          {state === "ok" && data && (
            <>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-gold-dark">Suivi de votre projet</p>
              <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-testid="tracking-title">
                Bonjour {data.first_name}, voici où en est votre site.
              </h1>
              <p className="mt-2 text-sm text-slate-600" data-testid="tracking-meta">
                Demande du {formatDate(data.created_at)}
                {data.offer && <> · Formule <strong className="text-navy">{data.offer}</strong></>}
                {data.status !== "publie" && data.status !== "perdue" && <> · Livraison estimée sous {data.delivery_estimate}</>}
              </p>

              {data.status === "perdue" ? (
                <p className="mt-8 rounded-2xl border border-navy/10 bg-cream px-5 py-4 text-sm text-slate-600" data-testid="tracking-closed">
                  Ce projet est clos. Envie de le relancer ? Appelez-nous, nous reprenons là où nous nous étions arrêtés.
                </p>
              ) : (
                <ol className="mt-10" data-testid="tracking-steps">
                  {data.steps.map((id, i) => <Step key={id} id={id} index={i} current={data.step_index} isLast={i === data.steps.length - 1} />)}
                </ol>
              )}

              {data.preview_url && data.status !== "publie" && data.status !== "perdue" && (
                <PreviewValidation token={token} data={data} onUpdate={(patch) => setData((d) => ({ ...d, ...patch }))} />
              )}

              {data.site_url && (
                <a
                  data-testid="tracking-site-link"
                  href={data.site_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-navy-light hover:shadow-lg hover:shadow-gold/25"
                >
                  Voir mon site en ligne <ExternalLink size={15} />
                </a>
              )}
            </>
          )}
          <div className="mt-8 flex flex-col items-center gap-2 border-t border-navy/10 pt-6 text-sm sm:flex-row sm:justify-center sm:gap-6">
            <a href="tel:+33784678802" className="flex items-center gap-2 font-semibold text-navy hover:text-gold-dark" data-testid="tracking-phone"><Phone size={14} /> 07 84 67 88 02</a>
            <a href="mailto:site.com@outlook.fr" className="flex items-center gap-2 text-slate-600 hover:text-gold-dark" data-testid="tracking-email"><Mail size={14} /> site.com@outlook.fr</a>
          </div>
        </div>
      </div>
    </div>
  );
}
